import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ReactNode } from "react";

import { FRAME, SITE_STEPS, marketingPalette } from "./tokens";
import FooterBrand from "./footer/FooterBrand";
import FooterLegal from "./footer/FooterLegal";
import FooterNav from "./footer/FooterNav";

/** The band's own edge, drawn on all four sides. */
const EDGE = alpha("#FFFFFF", 0.06);
/** The rule between the links and the legal strip. */
const RULE = alpha("#FFFFFF", 0.15);
const RULE_HEIGHT = "0.96px";

/**
 * `.footer_section` around `.main-footer_container`: the dark band and its
 * frame, which the paid-search pages' footer shares.
 *
 * The published page wraps the band in a bare `div` before the `section` that
 * paints it; one `footer` does the same job and gives the region a landmark it
 * did not have.
 *
 * The type size is set here rather than left to inherit because the link rows
 * take their height from it, not from the 14px links inside them.
 */
export function FooterBand({ children }: { children: ReactNode }) {
  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: marketingPalette.darkGround,
        border: `1px solid ${EDGE}`,
        fontSize: 16,
        lineHeight: "24px",
      }}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          // The published rule is a single `gap: 20px`, so the column half is
          // set too, inert while the column never wraps, wrong if it ever does.
          gap: 3.5,
          width: "100%",
          maxWidth: FRAME.footer,
          mx: "auto",
          px: "36px",
          pt: 5.5,
          pb: 5,
          textAlign: "left",
          position: "relative",
          zIndex: 3,
          [theme.breakpoints.down(SITE_STEPS.wide)]: { px: 4 },
          [theme.breakpoints.down(SITE_STEPS.card)]: { px: 3, pt: 5, pb: 3 },
          [theme.breakpoints.down(SITE_STEPS.tiny)]: { px: 2.5 },
        })}
      >
        {children}
      </Box>
    </Box>
  );
}

/** The site footer. */
export default function MarketingFooter() {
  return (
    <FooterBand>
      <Box
        sx={(theme) => ({
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          [theme.breakpoints.down(SITE_STEPS.wide)]: {
            flexDirection: "column",
            gap: 5,
          },
          // Inert once the row is a column, and left behind by the published
          // rule. Kept so a computed-style probe sees one band, not two.
          [theme.breakpoints.down(SITE_STEPS.tiny)]: { columnGap: 0 },
        })}
      >
        <FooterBrand />
        <FooterNav />
      </Box>
      <Box sx={{ width: "100%", height: RULE_HEIGHT, backgroundColor: RULE }} />
      <FooterLegal />
    </FooterBand>
  );
}
